
import { defineComponent, shallowRef, set, computed, watch, PropType } from 'vue'

// Composables
import { UseDateRange } from '@/use/date-range'
import { useUql, joinQuery, injectQueryStore } from '@/use/uql'
import { useActiveMetrics } from '@/metrics/use-metrics'
import { useTimeseries, useStyledTimeseries } from '@/metrics/use-query'

// Components
import GridItemFormPanes from '@/metrics/GridItemFormPanes.vue'
import SinglePanel from '@/components/SinglePanel.vue'
import PanelSection from '@/components/PanelSection.vue'
import UnitPicker from '@/components/UnitPicker.vue'
import LegendaryChart from '@/metrics/LegendaryChart.vue'
import MetricsPicker from '@/metrics/MetricsPicker.vue'
import MetricsQueryBuilder from '@/metrics/query/MetricsQueryBuilder.vue'
import TimeseriesStyleForm from '@/metrics/TimeseriesStyleForm.vue'

// Misc
import { requiredRule } from '@/util/validation'
import {
  defaultTimeseriesStyle,
  updateColumnMap,
  assignColors,
  emptyMetricColumn,
  ChartGridItem,
  ChartKind,
  StyledTimeseries,
  TimeseriesStyle,
  LegendType,
  LegendPlacement,
  LegendValue,
} from '@/metrics/types'

export default defineComponent({
  name: 'GridItemChartForm',
  components: {
    GridItemFormPanes,
    SinglePanel,
    PanelSection,
    LegendaryChart,
    UnitPicker,
    MetricsPicker,
    MetricsQueryBuilder,
    TimeseriesStyleForm,
  },

  props: {
    dateRange: {
      type: Object as PropType<UseDateRange>,
      required: true,
    },
    tableGrouping: {
      type: Array as PropType<string[]>,
      required: true,
    },
    gridItem: {
      type: Object as PropType<ChartGridItem>,
      required: true,
    },
  },

  setup(props, ctx) {
    const uql = useUql()
    const rules = { title: [requiredRule] }

    const chartKindItems = computed(() => {
      return [
        { text: 'Line chart', value: ChartKind.Line },
        { text: 'Area chart', value: ChartKind.Area },
        { text: 'Stacked area', value: ChartKind.StackedArea },
        { text: 'Bar chart', value: ChartKind.Bar },
        { text: 'Stacked bar', value: ChartKind.StackedBar },
      ]
    })
    const legendValueItems = computed(() => {
      return [
        { text: 'Avg', value: LegendValue.Avg },
        { text: 'Last', value: LegendValue.Last },
        { text: 'Min', value: LegendValue.Min },
        { text: 'Max', value: LegendValue.Max },
      ]
    })

    const activeMetrics = useActiveMetrics(computed(() => props.gridItem.params.metrics))

    const { where } = injectQueryStore()
    const timeseries = useTimeseries(() => {
      if (!props.gridItem.params.metrics.length || !props.gridItem.params.query) {
        return undefined
      }

      return {
        ...props.dateRange.axiosParams(),
        metric: props.gridItem.params.metrics.map((m) => m.name),
        alias: props.gridItem.params.metrics.map((m) => m.alias),
        query: joinQuery([props.gridItem.params.query, where.value]),
      }
    })

    const styledTimeseries = useStyledTimeseries(
      computed(() => timeseries.items),
      computed(() => props.gridItem.params.columnMap),
      computed(() => props.gridItem.params.timeseriesMap),
    )
    const currentTimeseries = shallowRef<StyledTimeseries[]>()
    const activeTimeseries = computed(() => {
      if (currentTimeseries.value) {
        return currentTimeseries.value
      }
      return styledTimeseries.value
    })

    watch(
      () => props.gridItem.params.query,
      (query) => {
        uql.query = query
      },
      { immediate: true },
    )

    watch(
      () => uql.query,
      (query) => {
        props.gridItem.params.query = query
      },
    )

    watch(
      () => timeseries.query,
      (query) => {
        if (query) {
          uql.setQueryInfo(query)
        }
      },
      { immediate: true },
    )

    watch(
      () => timeseries.columns,
      (columns) => {
        updateColumnMap(props.gridItem.params.columnMap, columns, emptyMetricColumn)
        assignColors(props.gridItem.params.columnMap, columns)
      },
    )

    function getTimeseriesStyle(ts: StyledTimeseries): TimeseriesStyle {
      if (!(ts.name in props.gridItem.params.timeseriesMap)) {
        set(props.gridItem.params.timeseriesMap, ts.name, {
          ...defaultTimeseriesStyle(),
          color: ts.color,
        })
      }
      return props.gridItem.params.timeseriesMap[ts.name]
    }

    return {
      LegendType,
      LegendPlacement,

      uql,

      rules,
      chartKindItems,
      legendValueItems,

      activeMetrics,
      timeseries,
      styledTimeseries,
      currentTimeseries,
      activeTimeseries,

      getTimeseriesStyle,
    }
  },
})
